import { useEffect, useMemo, useState } from 'react'
import { useStyles } from './styles'
import {
  Box,
  MenuItem,
  Container,
  Select,
  FormControl,
} from '@material-ui/core'
import { ToggleButtonGroup } from '@material-ui/lab'
import StudentsTablePagination from '../students-table-pagination'
import SegmentedToggleButton from 'components/segmented-toggle-button'
import CalendarTodayOutlined from 'components/material-symbol/icons/CalendarToday'
import EventAvailableOutlined from 'components/material-symbol/icons/EventAvailable'
import LinkIcon from 'components/material-symbol/icons/Link'
import SectionEmptyState from 'components/section-empty-state'
import { TonalButton } from 'components/empty-page-state'
import SocialShareDialog from 'components/social-share-dialog'
import { useAuth } from 'lib/auth'
import { gtmEvent } from 'lib/gtm'
import { isPgaLink, onReschedule, onPayment, onCancellation } from 'lib/utils'
import {
  parsePastSessions,
  parseUpcomingSessions,
  yearsSelect,
  filterByYear,
  getDefaultPastYear,
} from './utils'
import LoadingView from '../loading-view'
import StudentSessionRow from './sessions-components/student-session-row'
import useScheduleStyles from 'pages/pga-coach/dashboard/todays-schedule/styles'
import useEventStyles from 'pages/pga-coach/bookings/events/styles'
import { checkSessionStatus } from 'lib/sessions/utils'

const ROWS_PER_PAGE = 8

const StudentSessions = ({ sessions = {}, student }) => {
  const classes = useStyles()
  const scheduleClasses = useScheduleStyles()
  const eventClasses = useEventStyles()
  const { user } = useAuth()

  const { data: { studentEnrollments = [] } = {}, loading, refetch } = sessions

  const [sessionType, setSessionType] = useState('upcoming')
  const [year, setYear] = useState(null)
  const [page, setPage] = useState(0)

  const upcomingSessions = useMemo(() => {
    if (loading) return []
    return parseUpcomingSessions(studentEnrollments)
  }, [studentEnrollments, loading])

  const pastSessions = useMemo(() => {
    if (loading) return []
    return parsePastSessions(studentEnrollments)
  }, [studentEnrollments, loading])

  const pastYears = useMemo(() => yearsSelect(pastSessions), [pastSessions])

  const resolvedPastYear = useMemo(() => {
    if (year != null && pastYears.includes(Number(year))) {
      return Number(year)
    }

    return getDefaultPastYear(pastSessions, pastYears)
  }, [year, pastYears, pastSessions])

  useEffect(() => {
    if (sessionType !== 'past') return

    const yearIsValid = year != null && pastYears.includes(Number(year))

    if (!yearIsValid && resolvedPastYear != null && resolvedPastYear !== year) {
      setYear(resolvedPastYear)
    }
  }, [sessionType, year, pastYears, resolvedPastYear])

  const tabSessions = useMemo(() => {
    if (loading) return []

    if (sessionType === 'upcoming') {
      return upcomingSessions
    }

    if (resolvedPastYear == null) {
      return pastSessions
    }

    return filterByYear(pastSessions, resolvedPastYear)
  }, [loading, sessionType, upcomingSessions, pastSessions, resolvedPastYear])

  useEffect(() => {
    setPage(0)
  }, [sessionType, year, studentEnrollments])

  const handleSessionTypeChange = (_, newType) => {
    if (newType === null || newType === sessionType) return

    setSessionType(newType)

    if (newType === 'upcoming') {
      setYear(null)
    }
  }

  const handleRefetch = () => {
    refetch()
  }

  const handleSelectChange = ({ target }) => {
    setYear(Number(target.value))
  }

  function SessionTypeToggle() {
    return (
      <ToggleButtonGroup
        exclusive
        value={sessionType}
        onChange={handleSessionTypeChange}
        className={`${eventClasses.segmentControl} ${classes.sessionsSegmentControl}`}
        aria-label="Session timeframe"
      >
        <SegmentedToggleButton
          value="upcoming"
          groupValue={sessionType}
          aria-label="Upcoming"
          data-testid="student-sessions-tab-upcoming"
        >
          Upcoming
        </SegmentedToggleButton>
        <SegmentedToggleButton
          value="past"
          groupValue={sessionType}
          aria-label="Past"
          data-testid="student-sessions-tab-past"
        >
          Past
        </SegmentedToggleButton>
      </ToggleButtonGroup>
    )
  }

  function EmptyState() {
    const bookingUrl = user?.coach?.bookingUrl
    const showBookButton = bookingUrl !== null ? isPgaLink(bookingUrl) : false
    const isUpcomingTab = sessionType === 'upcoming'
    const emptyStateTitle = isUpcomingTab
      ? 'No Upcoming Sessions'
      : 'No Past Sessions'
    const emptyStateCaption =
      'Book a lesson or group event, or share your booking link with this student to get them scheduled.'

    const handleBookStudentClick = () => {
      gtmEvent({
        event: 'formSubmit',
        formCategory: 'book-session',
        formAction: 'clicked-book-session-button',
      })
      window.open(
        `${new URL(bookingUrl).pathname}?student=${student.id}`,
        '_blank',
        'noopener,noreferrer',
      )
    }

    return (
      <Box
        className={classes.sessionsEmptyStateContent}
        data-testid="student-sessions-empty-state"
      >
        <SectionEmptyState
          Icon={CalendarTodayOutlined}
          title={emptyStateTitle}
          caption={emptyStateCaption}
          bordered={false}
          action={
            bookingUrl ? (
              <Box className={classes.sessionsEmptyStateActions}>
                {showBookButton ? (
                  <TonalButton
                    startIcon={<EventAvailableOutlined />}
                    onClick={handleBookStudentClick}
                  >
                    Book Student
                  </TonalButton>
                ) : null}
                <SocialShareDialog
                  trigger={
                    <TonalButton startIcon={<LinkIcon />}>
                      Share Booking Link
                    </TonalButton>
                  }
                  shareUrl={bookingUrl}
                  shareTitle="Booking Link"
                  showBookingUrl
                  att="booking-link"
                />
              </Box>
            ) : null
          }
        />
      </Box>
    )
  }

  const pageCount = Math.ceil(tabSessions.length / ROWS_PER_PAGE) || 1
  const paginatedSessions = tabSessions.slice(
    page * ROWS_PER_PAGE,
    (page + 1) * ROWS_PER_PAGE,
  )

  const sessionRows = paginatedSessions.map(session => {
    const { isPaid, isCanceled, isLessonCredit } = checkSessionStatus({
      isCanceled: session.isCanceled,
      enrollments: [session],
    })

    return (
      <StudentSessionRow
        session={session}
        student={student}
        isCanceled={isCanceled}
        isPaid={isPaid}
        isLessonCredit={isLessonCredit}
        refetch={handleRefetch}
        key={session.id}
        onReschedule={() => onReschedule(handleRefetch)}
        onPayment={() => onPayment(handleRefetch)}
        onCancellation={() => onCancellation(handleRefetch)}
      />
    )
  })

  function SessionsTableToolbar() {
    return (
      <Box className={classes.sessionsTableToolbar}>
        <Box className={classes.sessionsTableToolbarControls}>
          <SessionTypeToggle />
          {sessionType === 'past' && pastYears.length ? (
            <Box className={classes.yearFilter}>
              <FormControl size="small" fullWidth>
                <Select
                  id="select-past-year"
                  SelectDisplayProps={{
                    'data-testid': 'student-sessions-year-filter',
                  }}
                  value={resolvedPastYear ?? ''}
                  onChange={handleSelectChange}
                >
                  {pastYears.map(y => (
                    <MenuItem value={y} key={y}>
                      {y}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          ) : null}
        </Box>
        <Box className={classes.sessionsToolbarActions}>
          <StudentsTablePagination
            page={page}
            pageCount={pageCount}
            rowsPerPage={ROWS_PER_PAGE}
            totalCount={tabSessions.length}
            onPageChange={setPage}
          />
        </Box>
      </Box>
    )
  }

  if (loading && !studentEnrollments.length) {
    return <LoadingView />
  }

  const hasSessions = tabSessions.length > 0

  return (
    <Container className={classes.sessionsContainer}>
      <Box className={scheduleClasses.card}>
        <SessionsTableToolbar />
        {hasSessions ? (
          <Box
            className={`${scheduleClasses.list} ${classes.sessionsList}`}
            data-testid="student-sessions-list"
          >
            {sessionRows}
          </Box>
        ) : (
          <EmptyState />
        )}
      </Box>
    </Container>
  )
}

export default StudentSessions
